/**
 * Referência de preço de mercado (seção 10.10 do planejamento).
 *
 * O padrão é MEDIANA, não a maior oferta. A regra de negócio que justifica
 * este módulo inteiro: um único anúncio de preço elevado não torna um negócio
 * viável (RN-087).
 */

import { BPS, arredondar, emBps } from './inteiros.js';

export type ModoReferencia =
  | 'MENOR_VALIDA'
  | 'MEDIA'
  | 'MEDIANA'
  | 'MEDIA_APARADA'
  | 'ANUNCIO_MANUAL'
  | 'PRECO_USUARIO';

export function menorValida(precosCents: readonly number[]): number {
  exigirNaoVazio(precosCents);
  return Math.min(...precosCents);
}

export function media(precosCents: readonly number[]): number {
  exigirNaoVazio(precosCents);
  return arredondar(
    precosCents.reduce((a, b) => a + b, 0),
    precosCents.length,
  );
}

export function mediana(precosCents: readonly number[]): number {
  exigirNaoVazio(precosCents);
  const ord = [...precosCents].sort((a, b) => a - b);
  const meio = Math.floor(ord.length / 2);
  const central = ord[meio]!;
  return ord.length % 2 === 1 ? central : arredondar(ord[meio - 1]! + central, 2);
}

/**
 * Média aparada: descarta uma fatia de cada extremo antes de tirar a média.
 * Quando a amostra é pequena demais para aparar sem esvaziar, cai na mediana —
 * que é a escolha resistente equivalente.
 */
export function mediaAparada(precosCents: readonly number[], aparaBps = 1000): number {
  exigirNaoVazio(precosCents);
  const ord = [...precosCents].sort((a, b) => a - b);
  const k = Math.floor((ord.length * aparaBps) / BPS);
  const restante = ord.slice(k, ord.length - k);
  return restante.length === 0 ? mediana(ord) : media(restante);
}

/**
 * Dispersão entre o menor e o maior anúncio válido, em pontos-base.
 * Alimenta a RN-087: acima do limiar, a análise cai para REQUER_REVISAO.
 */
export function dispersaoBps(precosCents: readonly number[]): number | null {
  if (precosCents.length < 2) return null;
  const menor = Math.min(...precosCents);
  const maior = Math.max(...precosCents);
  if (menor <= 0) return null;
  return emBps(maior - menor, menor);
}

export function calcularReferencia(modo: ModoReferencia, precosCents: readonly number[]): number {
  switch (modo) {
    case 'MENOR_VALIDA':
      return menorValida(precosCents);
    case 'MEDIA':
      return media(precosCents);
    case 'MEDIANA':
      return mediana(precosCents);
    case 'MEDIA_APARADA':
      return mediaAparada(precosCents);
    case 'ANUNCIO_MANUAL':
    case 'PRECO_USUARIO':
      throw new Error(`modo ${modo} recebe o preço direto, não uma cesta de anúncios`);
  }
}

/** Preço por unidade de um anúncio em pacote (RN-078). Base de toda comparação. */
export function precoNormalizado(precoCents: number, qtdPorPacoteMilli: number): number {
  if (qtdPorPacoteMilli <= 0) throw new Error('quantidade por pacote deve ser maior que zero');
  return arredondar(precoCents * 1000, qtdPorPacoteMilli);
}

function exigirNaoVazio(precos: readonly number[]): void {
  if (precos.length === 0) throw new Error('cesta de anúncios vazia — use DADOS_INSUFICIENTES em vez de calcular');
}
